import React, { Fragment, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import find from 'lodash/find';
import filter from 'lodash/filter';
import map from 'lodash/map';

import type { ProjectsTasksTreeSelectFieldControlProps } from './ProjectsTasksTreeSelectField.types';
import type { CacheFetchShowProjectsTaskChildProjectsTasksQueryResponse } from '../../queries/fetchCacheShowProjectsTaskChildProjectsTasks.query';
import type {
  FormatOptionLabelType,
  MultiSelectChangeCallbackType,
  MultiSelectDataType,
  MultiSelectMixedDataType
} from '../../../../helpers/MultiSelect/types';
import type { MultiSelectFieldProps } from '../../../../helpers/FormFields/MultiSelectField';

import { CACHE_FETCH_SHOW_PROJECTS_TASK_CHILD_PROJECTS_TASKS_QUERY } from '../../queries/fetchCacheShowProjectsTaskChildProjectsTasks.query';
import { ProjectsTaskCache } from '../../ProjectsTaskCache';

import { useCacheProjectsTask } from '../../hooks/useCacheProjectsTask';
import { useProjectsTasksSelectDeepTree } from '../../hooks/useProjectsTasksSelectDeepTree';

import { MultiSelect } from '../../../../helpers/MultiSelect';
import { AlertMessage } from '../../../../helpers/AlertMessage';
import { Translate } from '../../../../helpers/Translate';

import { projectsKeys } from '../../../../locales/keys';

interface ProjectsTasksTreeSelectFieldControlInternalProps {
  value: unknown;
  onChange: (...event: unknown[]) => void;
  projectsTask: CacheFetchShowProjectsTaskChildProjectsTasksQueryResponse | null;
  projectsTaskLoading: boolean;
  projectsTaskError?: string;
  multi: boolean;
  valueType: 'uuid' | 'nanoId' | 'originalId';
  closeMenuOnSelect?: boolean;
  disabled?: boolean;
  i18nLabel?: string | null;
  i18nPlaceholder?: string;
  inputWrapperClassName?: string | null;
  labelClassName?: string;
  name: string;
  error?: string;
  isOptionDisabled?: boolean;
  emptyValue?: MultiSelectDataType | null;
  isClearable?: boolean;
}

function ProjectsTasksTreeSelectFieldControl({
  value,
  onChange,
  projectsTask,
  projectsTaskLoading,
  projectsTaskError,
  multi,
  valueType,
  closeMenuOnSelect,
  disabled,
  i18nLabel,
  i18nPlaceholder,
  inputWrapperClassName,
  labelClassName,
  name,
  error,
  emptyValue,
  isClearable
}: ProjectsTasksTreeSelectFieldControlInternalProps) {
  const { data, plainData, onInputChange } = useProjectsTasksSelectDeepTree({
    projectsTask,
    valueType
  });

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (
      newValue: MultiSelectMixedDataType | MultiSelectMixedDataType[] | null
    ) => {
      if (multi) {
        const arrayValue = Array.isArray(newValue)
          ? newValue
          : newValue
          ? [newValue]
          : [];
        onChange(
          map(arrayValue, (item) => ('value' in item ? item.value : item))
        );
      } else {
        const singleValue = newValue as MultiSelectDataType | null;
        onChange(singleValue?.value || null);
      }
    },
    [onChange, multi]
  );

  const selectValue = useMemo(() => {
    const plainDataWithEmptyValue = emptyValue
      ? [...plainData, emptyValue]
      : plainData;

    if (multi) {
      const valuesArray = Array.isArray(value) ? value : value ? [value] : [];
      return filter(plainDataWithEmptyValue, (option) =>
        (valuesArray as (string | number)[]).includes(option.value)
      );
    }
    return find(plainDataWithEmptyValue, ['value', value]) || null;
  }, [value, plainData, multi, emptyValue]);

  const handleIsOptionDisabled = useCallback(
    (option: MultiSelectMixedDataType) => {
      if ('type' in option && option.type === 'project') {
        return true;
      }

      return false;
    },
    []
  );

  const formatOptionLabel = useCallback<FormatOptionLabelType>(
    (option, { context }) => {
      if (context === 'menu' && 'type' in option && option.type === 'project') {
        return (
          <div className="italic flex items-center justify-between gap-2">
            <span className="truncate">{option.label}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              <Translate id={projectsKeys.group} />
            </span>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">{option.label}</span>
        </div>
      );
    },
    []
  );

  return (
    <Fragment>
      <MultiSelect
        classNamePrefix="av"
        closeMenuOnSelect={closeMenuOnSelect ?? !multi}
        formatOptionLabel={formatOptionLabel}
        data={data}
        disabled={disabled}
        error={error || projectsTaskError}
        i18nLabel={i18nLabel}
        i18nPlaceholder={i18nPlaceholder}
        inputWrapperClassName={inputWrapperClassName}
        isClearable={isClearable}
        isSearchable={true}
        isOptionDisabled={handleIsOptionDisabled}
        labelClassName={labelClassName}
        multi={multi}
        name={name}
        onChange={handleChange}
        onInputChange={onInputChange}
        optionsLoading={projectsTaskLoading}
        value={selectValue}
        emptyValue={emptyValue}
        menuPosition="fixed"
      />
      {projectsTaskError && (
        <AlertMessage addClassName="mt-2" message={projectsTaskError} />
      )}
    </Fragment>
  );
}

function ProjectsTasksTreeSelectField<T>({
  control,
  name,
  i18nPlaceholder,
  inputWrapperClassName,
  i18nLabel,
  labelClassName,
  disabled,
  defaultValue,
  rootProjectNanoId,
  multi,
  valueType = 'uuid',
  closeMenuOnSelect,
  emptyValue
}: ProjectsTasksTreeSelectFieldControlProps<T> & MultiSelectFieldProps<T>) {
  const { projectsTask, projectsTaskLoading, projectsTaskError } =
    useCacheProjectsTask<CacheFetchShowProjectsTaskChildProjectsTasksQueryResponse>(
      {
        cacheKey: ProjectsTaskCache.showProjectTreeCacheKey(),
        uuid: rootProjectNanoId,
        query: CACHE_FETCH_SHOW_PROJECTS_TASK_CHILD_PROJECTS_TASKS_QUERY
      }
    );

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as any} // eslint-disable-line @typescript-eslint/no-explicit-any
      render={({
        field: { onChange, value, name: fieldName },
        fieldState: { error }
      }) => (
        <ProjectsTasksTreeSelectFieldControl
          value={value}
          onChange={onChange}
          projectsTask={projectsTask}
          projectsTaskLoading={projectsTaskLoading}
          projectsTaskError={projectsTaskError}
          multi={multi}
          valueType={valueType}
          closeMenuOnSelect={closeMenuOnSelect}
          disabled={disabled}
          emptyValue={emptyValue}
          i18nLabel={i18nLabel}
          i18nPlaceholder={i18nPlaceholder}
          inputWrapperClassName={inputWrapperClassName}
          labelClassName={labelClassName}
          name={fieldName}
          error={error?.message}
        />
      )}
    />
  );
}

export default ProjectsTasksTreeSelectField;
